/**
 * Fishing Ports data layer. Loads ports via API and passes to MapController; optional "simulation only" filter.
 * Syncs from layout so layer shows when provided in initialLayers even before tab is opened.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';
import { useApi } from '../../../api/api';

interface PortRecord {
  [key: string]: unknown;
}

export default function FishingPortsDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { slug, isReady, isActive, setActive, mapController } = useDataLayer(props);
  const { getFishingPorts } = useApi();
  const [portsLoading, setPortsLoading] = useState(false);
  const [portsCount, setPortsCount] = useState(0);
  const [showSimulationPortsOnly, setShowSimulationPortsOnly] = useState(false);
  const syncedFromLayoutRef = useRef(false);

  // When layer is in layout, mapController has it active but our isActive may not have synced yet.
  useLayoutEffect(() => {
    if (syncedFromLayoutRef.current || !setActive || !mapController?.isReady()) return;
    if (mapController.isLayerActive(slug) && !isActive) {
      setActive(true);
      syncedFromLayoutRef.current = true;
    }
  }, [slug, isActive, setActive, mapController]);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      setPortsLoading(true);
      try {
        const response = await getFishingPorts();
        let ports: PortRecord[] = [];
        const data = response?.data as
          | { success?: boolean; results?: PortRecord[] }
          | PortRecord[]
          | undefined;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          if (data.success && Array.isArray(data.results)) {
            ports = data.results;
          }
        } else if (Array.isArray(data)) {
          ports = data;
        }
        if (!cancelled) {
          setPortsCount(ports.length);
          if (mapController?.isReady()) mapController.setPortsData(ports);
        }
      } catch (e: unknown) {
        if (!cancelled) setPortsCount(0);
      } finally {
        if (!cancelled) setPortsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [getFishingPorts, mapController]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">Fishing Ports</span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '12px',
          fontStyle: 'italic',
        }}
      >
        Plot fishing ports in North America (
        {portsLoading ? 'Loading...' : `${portsCount} ports`})
      </div>
      <div className="form-group">
        <label
          className="checkbox-label"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setActive(e.target.checked)
            }
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show Fishing Ports</span>
        </label>
      </div>
      {isActive && (
        <div
          style={{
            marginLeft: '20px',
            marginTop: '8px',
            paddingLeft: '8px',
            borderLeft: '2px solid #e5e7eb',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '11px',
              color: '#6b7280',
            }}
          >
            <input
              type="checkbox"
              checked={showSimulationPortsOnly}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setShowSimulationPortsOnly(e.target.checked)
              }
              style={{ marginRight: '8px', transform: 'scale(1.1)' }}
            />
            Show only ports from simulation data
          </label>
        </div>
      )}
    </div>
  );
}
